/**
 * PokemonCard Component - Pokemon draft card with animations
 * Supports blind mode: hides sprite/name, shows only clues.
 */

import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { showToast } from './Toast';
import { translateType, getTypeColor } from '../utils/typeUtils';
import { MAX_TEAM_SIZE } from '../types/pokemon';
import type { Pokemon } from '../types/pokemon';
import InfoOverlay from './InfoOverlay';

interface PokemonCardProps {
  pokemon: Pokemon;
  index: number;
}

export function PokemonCard({ pokemon, index }: PokemonCardProps) {
  const { state, dispatch } = useApp();
  const [isShiny] = useState(() => Math.floor(Math.random() * 2048) === 0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const isInTeam = state.teams.some(t => t.pokemon.some(p => p.id === pokemon.id));
  const blindMode = state.settings.blindMode && !revealed;
  const clues = state.settings.blindClues;
  const accentColor = getTypeColor(pokemon.types[0]);
  const accentColor2 = pokemon.types[1] ? getTypeColor(pokemon.types[1]) : accentColor;

  const handleCardClick = () => {
    if (isInTeam) {
      showToast('Deja selectionne !', 'warning');
      return;
    }

    // In blind mode: first click reveals, second click picks
    if (state.settings.blindMode && !revealed) {
      setRevealed(true);
      return;
    }

    const playerIdx = state.currentPlayer === 1 ? 0 : 1;
    if (state.teams[playerIdx].pokemon.length >= MAX_TEAM_SIZE) {
      showToast(`Equipe ${state.currentPlayer} complete !`, 'warning');
      return;
    }
    dispatch({ type: 'PICK_POKEMON', pokemon });
    showToast(`Joueur ${state.currentPlayer} a selectionne ${pokemon.nameFr} !`, 'success');
  };

  const bst = pokemon.stats.reduce((sum, s) => sum + s.value, 0);

  return (
    <div
      className={`relative w-[340px] shrink-0 animate-card-drop cursor-pointer group ${isShiny && !blindMode ? 'shiny-sparkle' : ''}`}
      style={{
        animationDelay: `${index * 150}ms`,
        filter: isInTeam ? 'grayscale(0.6) blur(1px)' : 'none',
        pointerEvents: isInTeam ? 'none' : 'auto',
        opacity: isInTeam ? 0.35 : undefined,
        transition: 'filter 0.3s ease, opacity 0.3s ease, transform 0.25s ease',
        transform: isHovered && !isInTeam ? 'translateY(-8px) scale(1.03)' : 'translateY(0) scale(1)',
      }}
      onClick={handleCardClick}
      onMouseEnter={() => { setIsHovered(true); if (!isInTeam && !blindMode) setShowOverlay(true); }}
      onMouseLeave={() => { setIsHovered(false); setShowOverlay(false); }}
    >
      <div
        className="rounded-2xl overflow-hidden relative"
        style={{
          background: blindMode
            ? 'linear-gradient(145deg, rgba(30,30,50,0.98) 0%, rgba(15,15,35,0.98) 100%)'
            : `linear-gradient(145deg, rgba(255,255,255,0.97) 0%, rgba(240,248,255,0.97) 60%, ${accentColor}15 100%)`,
          border: blindMode ? '3px solid rgba(255,255,255,0.1)' : `3px solid ${accentColor}60`,
          boxShadow: isHovered
            ? blindMode
              ? '0 12px 40px rgba(0,0,0,0.5), 0 0 30px rgba(100,100,255,0.15)'
              : `0 12px 40px rgba(0,0,0,0.4), 0 0 30px ${accentColor}30, inset 0 1px 0 rgba(255,255,255,0.8)`
            : blindMode
              ? '0 6px 20px rgba(0,0,0,0.5)'
              : `0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.8)`,
          transition: 'box-shadow 0.3s ease, background 0.5s ease, border-color 0.5s ease',
        }}
      >
        {/* Top gradient bar */}
        <div
          className="h-2 transition-all duration-500"
          style={{
            background: blindMode
              ? 'linear-gradient(90deg, #334, #445, #334)'
              : `linear-gradient(90deg, ${accentColor}, ${accentColor2}, ${accentColor})`,
            backgroundSize: '200% 100%',
            animation: isHovered ? 'gradientSlide 2s linear infinite' : 'none',
          }}
        />

        {/* Shiny Banner - only when not blind */}
        {isShiny && !blindMode && (
          <div className="bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 text-center py-1 text-[9px] font-retro text-white"
            style={{ boxShadow: '0 2px 10px rgba(255,200,0,0.5)', animation: 'shimmer 2s ease-in-out infinite' }}>
            ★ SHINY ★
          </div>
        )}

        {/* Info Overlay - only when not blind */}
        {showOverlay && !blindMode && <InfoOverlay pokemon={pokemon} />}

        <div className="p-5 pb-4">

          {/* ===== BLIND MODE CONTENT ===== */}
          {blindMode ? (
            <>
              {/* Blind header - ID + Gen based on clues */}
              <div className="flex items-center justify-between mb-3">
                {clues.showId ? (
                  <span className="font-retro text-[10px] text-white/60 px-2 py-0.5 rounded-md bg-white/10">
                    {formatPokemonId(pokemon.id)}
                  </span>
                ) : <span />}
                {clues.showGeneration && (
                  <span className="font-retro text-[9px] text-white/30">
                    Gen {pokemon.generation}
                  </span>
                )}
              </div>

              {/* Mystery sprite area */}
              <div
                className="relative w-48 h-48 mx-auto mb-4 rounded-xl overflow-hidden flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(60,60,100,0.5) 0%, rgba(20,20,40,0.8) 70%)',
                  border: '2px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="text-7xl opacity-30 select-none"
                  style={{ animation: 'pulse-glow 3s ease-in-out infinite', filter: 'blur(1px)' }}>
                  ?
                </span>
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 rounded-full bg-white/20"
                      style={{
                        left: `${15 + Math.random() * 70}%`,
                        top: `${15 + Math.random() * 70}%`,
                        animation: `homeFloat ${3 + i * 0.5}s ease-in-out infinite`,
                        animationDelay: `${i * 0.4}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Type badges */}
              {clues.showTypes && (
                <div className="flex gap-2 justify-center mb-3">
                  {pokemon.types.map((type) => (
                    <TypeBadge key={type} type={type} />
                  ))}
                </div>
              )}

              {/* Category */}
              {clues.showCategory && (
                <div className="text-center mb-3">
                  <span className="font-retro text-[9px] text-white/40 italic">
                    {pokemon.category}
                  </span>
                </div>
              )}

              {/* Pokedex description */}
              {clues.showDescription && pokemon.description && (
                <div className="text-center px-2 mb-3 py-2 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-[11px] text-white/50 leading-relaxed font-display">
                    "{pokemon.description}"
                  </p>
                </div>
              )}

              {/* No clues at all? Show a minimal hint */}
              {!clues.showId && !clues.showTypes && !clues.showGeneration && !clues.showCategory && !clues.showDescription && (
                <div className="text-center mb-3">
                  <span className="font-retro text-[9px] text-white/20">
                    Aucun indice
                  </span>
                </div>
              )}

              {/* Click to reveal hint */}
              <div className="text-center">
                <span className="font-retro text-[8px] text-poke-yellow/50 animate-pulse">
                  Cliquer pour reveler
                </span>
              </div>
            </>
          ) : (
            /* ===== NORMAL MODE CONTENT ===== */
            <>
              {/* ID + BST row */}
              <div className="flex items-center justify-between mb-3">
                <span
                  className="font-retro text-[10px] text-white px-2 py-0.5 rounded-md"
                  style={{
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                    boxShadow: `0 2px 8px ${accentColor}40`,
                  }}
                >
                  {formatPokemonId(pokemon.id)}
                </span>
                <span className="font-retro text-[9px] text-gray-400">
                  BST {bst}
                </span>
              </div>

              {/* Pokemon Image */}
              <div
                className="relative w-48 h-48 mx-auto mb-3 rounded-xl overflow-hidden"
                style={{
                  background: `radial-gradient(circle at 50% 40%, ${accentColor}15, transparent 70%), linear-gradient(135deg, #f0f7ff, #e8f0fe)`,
                  border: `2px solid ${accentColor}30`,
                  boxShadow: `inset 0 2px 8px rgba(0,0,0,0.06), 0 0 20px ${accentColor}10`,
                }}
              >
                <div
                  className="absolute inset-0 rounded-xl transition-opacity duration-300"
                  style={{
                    opacity: isHovered ? 1 : 0,
                    background: `conic-gradient(from 0deg, transparent, ${accentColor}20, transparent, ${accentColor}20, transparent)`,
                    animation: isHovered ? 'rotateRing 3s linear infinite' : 'none',
                  }}
                />
                <div
                  className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-white/60 to-transparent z-10"
                  style={{ animation: 'scan 3s linear infinite' }}
                />
                <img
                  src={isShiny ? (pokemon.spriteShiny || pokemon.sprite) : pokemon.sprite}
                  alt={pokemon.nameFr}
                  className={`relative z-10 w-full h-full object-contain drop-shadow-xl transition-transform duration-500 ${revealed ? 'animate-reveal' : ''}`}
                  loading="lazy"
                  style={{
                    imageRendering: 'pixelated',
                    transform: isHovered ? 'scale(1.15) rotate(-2deg)' : 'scale(1)',
                    filter: isHovered ? `drop-shadow(0 4px 12px ${accentColor}50)` : 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
                    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease',
                  }}
                  onError={(e) => {
                    e.currentTarget.src = pokemon.spriteAnimated || `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.id}.png`;
                  }}
                />
              </div>

              {/* Pokemon Name */}
              <div
                className={`text-center mb-3 py-2 px-3 rounded-lg relative overflow-hidden ${revealed ? 'animate-reveal' : ''}`}
                style={{
                  background: `linear-gradient(135deg, ${accentColor}dd, ${accentColor2}bb)`,
                  boxShadow: `0 3px 12px ${accentColor}40, inset 0 1px 0 rgba(255,255,255,0.3)`,
                }}
              >
                <h2 className="font-retro text-base text-white uppercase tracking-wide relative z-10"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.4)' }}>
                  {pokemon.nameFr}
                </h2>
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  style={{
                    animation: isHovered ? 'shineSwipe 1.5s ease-in-out infinite' : 'none',
                    transform: 'skewX(-20deg)',
                  }}
                />
              </div>

              {/* Types */}
              <div className="flex gap-2 justify-center mb-2">
                {pokemon.types.map((type) => (
                  <TypeBadge key={type} type={type} />
                ))}
              </div>

              {/* Mini stat bars - visible on hover */}
              <div
                className="transition-all duration-300 overflow-hidden"
                style={{
                  maxHeight: isHovered ? '60px' : '0px',
                  opacity: isHovered ? 1 : 0,
                  marginTop: isHovered ? '8px' : '0px',
                }}
              >
                <div className="grid grid-cols-3 gap-x-3 gap-y-1">
                  {pokemon.stats.slice(0, 6).map((stat) => (
                    <MiniStatBar key={stat.name} name={statLabel(stat.name)} value={stat.value} color={accentColor} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom accent line */}
        <div
          className="h-1"
          style={{
            background: blindMode
              ? 'linear-gradient(90deg, #334, #445, #334)'
              : `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
            transition: 'background 0.5s ease',
          }}
        />
      </div>
    </div>
  );
}

function MiniStatBar({ name, value, color }: { name: string; value: number; color: string }) {
  const pct = Math.min((value / 255) * 100, 100);
  return (
    <div className="flex items-center gap-1">
      <span className="font-retro text-[7px] text-gray-500 w-6 text-right">{name}</span>
      <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color, opacity: 0.7 }}
        />
      </div>
      <span className="font-retro text-[7px] text-gray-400 w-5">{value}</span>
    </div>
  );
}

function statLabel(name: string): string {
  const map: Record<string, string> = {
    hp: "PV", attack: "ATK", defense: "DEF",
    "special-attack": "SPA", "special-defense": "SPD", speed: "SPE",
  };
  return map[name] || name.slice(0, 3).toUpperCase();
}

function TypeBadge({ type }: { type: string }) {
  const typeColor = getTypeColor(type);
  return (
    <span
      className={`type-${type} px-3 py-1 text-white font-retro text-[9px] uppercase tracking-wide rounded-lg transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 cursor-pointer`}
      style={{
        border: `2px solid rgba(0,0,0,0.2)`,
        boxShadow: `0 2px 8px ${typeColor}40, inset 0 1px 0 rgba(255,255,255,0.25)`,
      }}
    >
      {translateType(type)}
    </span>
  );
}

function formatPokemonId(id: number): string {
  return `#${String(id).padStart(3, '0')}`;
}

export default PokemonCard;
